import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  SimpleGrid,
  Card,
  CardBody,
  CardFooter,
  Divider,
  Stack,
  Image,
  useColorModeValue,
  Alert,
  AlertIcon,
  Spinner,
  Link,
  Icon,
  Flex
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ExternalLinkIcon, TimeIcon } from '@chakra-ui/icons';
import supabase from '../utils/supabaseUtils';

const ApprovalsList = () => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  useEffect(() => {
    const fetchApprovals = async () => {
      try {
        setLoading(true);
        
        // Get current user
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        if (!userData?.user?.id) {
          throw new Error('User not authenticated');
        }
        
        // Fetch user's approvals
        const { data: approvalsData, error: approvalsError } = await supabase
          .from('image_approvals')
          .select(`
            *,
            user_uploads(id, image_url, file_url, watermarked_url, approval_status)
          `)
          .eq('user_id', userData.user.id)
          .or('status.eq.pending,status.eq.in_progress,status.eq.completed')
          .order('created_at', { ascending: false });
          
        if (approvalsError) throw approvalsError;
        
        // Get event data for each approval
        const enhancedApprovals = await Promise.all(
          (approvalsData || []).map(async (approval) => {
            // Skip if no event_id
            if (!approval.event_id) return approval;
            
            try {
              const { data: eventData } = await supabase
                .from('events')
                .select('name, event_type')
                .eq('id', approval.event_id)
                .single();
                
              return {
                ...approval,
                event_name: eventData?.name || 'Unknown Event',
                event_type: eventData?.event_type || 'Event'
              };
            } catch (err) {
              console.error("Error fetching event data:", err);
              return approval;
            }
          })
        );
        
        setApprovals(enhancedApprovals || []);
      } catch (err) {
        console.error('Error fetching approvals:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchApprovals();
  }, []);
  
  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Get status badge color based on status
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'yellow';
      case 'in_progress': return 'blue';
      case 'completed': return 'green';
      case 'expired': return 'red';
      case 'cancelled': return 'gray';
      default: return 'gray';
    }
  };
  
  // Find a thumbnail image for the approval
  const getApprovalThumbnail = (approval) => {
    if (!approval.user_uploads || !Array.isArray(approval.user_uploads)) {
      return null;
    }
    
    // Try to find an approved image first
    const approvedImage = approval.user_uploads.find(img => 
      img.approval_status === 'approved' && (img.image_url || img.file_url || img.watermarked_url)
    );
    
    if (approvedImage) {
      return approvedImage.watermarked_url || approvedImage.image_url || approvedImage.file_url;
    }
    
    // Otherwise use the first image with any URL
    const anyImage = approval.user_uploads.find(img => 
      img.image_url || img.file_url || img.watermarked_url
    );
    
    return anyImage 
      ? (anyImage.watermarked_url || anyImage.image_url || anyImage.file_url)
      : null;
  };
  
  // Handle click to go to approval page
  const goToApproval = (approvalId) => {
    navigate(`/approve/${approvalId}`);
  };
  
  if (loading) {
    return (
      <Container maxW="container.lg" py={10} centerContent>
        <Spinner size="xl" color="blue.500" />
        <Text mt={4}>Loading your approvals...</Text>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxW="container.lg" py={10}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Error loading approvals</Text>
            <Text>{error}</Text>
          </Box>
        </Alert>
        <Button mt={4} colorScheme="blue" onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxW="container.lg" py={10}>
      <Heading size="lg" mb={6}>Your Approvals</Heading>
      
      {approvals.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">No approvals found</Text>
            <Text>You don't have any photo approvals yet.</Text>
          </Box>
        </Alert>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {approvals.map(approval => (
            <Card 
              key={approval.id} 
              overflow="hidden" 
              variant="outline" 
              borderColor={borderColor}
              transition="all 0.2s"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
            >
              <Box 
                height="200px" 
                overflow="hidden" 
                bg="gray.100" 
                position="relative"
              >
                {getApprovalThumbnail(approval) ? (
                  <Image
                    src={getApprovalThumbnail(approval)}
                    alt="Approval thumbnail"
                    objectFit="cover"
                    width="100%"
                    height="100%"
                  />
                ) : (
                  <Flex 
                    height="100%" 
                    width="100%" 
                    justify="center" 
                    align="center" 
                    bg="blue.50"
                  >
                    <Text color="blue.500" fontWeight="bold">
                      {approval.event_name || 'Photo Approval'}
                    </Text>
                  </Flex>
                )}
                
                <Badge 
                  position="absolute" 
                  top={2} 
                  right={2}
                  colorScheme={getStatusColor(approval.status)}
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  {approval.status}
                </Badge>
              </Box>
              
              <CardBody>
                <Stack spacing={2}>
                  <Heading size="md">{approval.event_name || 'Photo Approval'}</Heading>
                  
                  <HStack>
                    <Icon as={TimeIcon} color="gray.500" />
                    <Text color="gray.500" fontSize="sm">
                      Created: {formatDate(approval.created_at)}
                    </Text>
                  </HStack>
                  
                  <Text fontSize="sm">
                    {approval.user_uploads?.length || 0} image(s) in this approval
                  </Text>
                </Stack>
              </CardBody>
              
              <Divider color={borderColor} />
              
              <CardFooter>
                <Button 
                  rightIcon={<ExternalLinkIcon />} 
                  colorScheme="blue" 
                  onClick={() => goToApproval(approval.id)}
                  width="100%"
                >
                  Review Approval
                </Button>
              </CardFooter>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Container>
  );
};

export default ApprovalsList; 